import React from "react";
import { render, screen, fireEvent } from "@testing-library/react";
import "@testing-library/jest-dom";

import { HeartbeatPanel } from "./HeartbeatPanel";
import { useHeartbeats } from "../hooks/useHeartbeats";

jest.mock("../hooks/useHeartbeats", () => ({
  useHeartbeats: jest.fn(),
}));

jest.mock("./HeartbeatEditor", () => ({
  HeartbeatEditor: ({ open }: { open: boolean }) =>
    open ? <div data-testid="heartbeat-editor">Heartbeat editor</div> : null,
}));

const mockUseHeartbeats = useHeartbeats as jest.Mock;

function buildHookResult(overrides: Partial<Record<string, unknown>> = {}) {
  return {
    items: [],
    schedulerAvailable: true,
    loading: false,
    error: null,
    refresh: jest.fn(),
    cancel: jest.fn(),
    edit: jest.fn(),
    ...overrides,
  };
}

function buildHeartbeat(taskId = "hb-1") {
  return {
    task_id: taskId,
    kind: "cron" as const,
    status: "active",
    next_fire_at: "2026-04-21T09:00:00Z",
    prompt: "Check weekly pipeline changes",
    conversation_id: 123,
  };
}

describe("HeartbeatPanel", () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  it("hides the panel when there are no scheduled heartbeats", () => {
    mockUseHeartbeats.mockReturnValue(buildHookResult());

    render(<HeartbeatPanel workspaceId="ws-1" isAdmin={true} />);

    expect(screen.queryByTestId("heartbeat-panel")).not.toBeInTheDocument();
  });

  it("renders the sticky panel when workspace heartbeats are present", () => {
    mockUseHeartbeats.mockReturnValue(
      buildHookResult({ items: [buildHeartbeat()] }),
    );

    render(<HeartbeatPanel workspaceId="ws-1" isAdmin={true} />);

    expect(screen.getByTestId("heartbeat-panel")).toBeInTheDocument();
    expect(screen.getByTestId("heartbeat-panel-toggle")).toHaveTextContent(
      "Heartbeats 1/5",
    );
    expect(
      screen.queryByText(/Check weekly pipeline changes/i),
    ).not.toBeInTheDocument();
  });

  it("expands the panel contents when the header is clicked", () => {
    mockUseHeartbeats.mockReturnValue(
      buildHookResult({ items: [buildHeartbeat()] }),
    );

    render(<HeartbeatPanel workspaceId="ws-1" isAdmin={true} />);

    fireEvent.click(screen.getByTestId("heartbeat-panel-toggle"));

    expect(screen.getByTestId("heartbeat-panel-list")).toBeInTheDocument();
    expect(
      screen.getByText(/Visible across this workspace/i),
    ).toBeInTheDocument();
    expect(
      screen.getByText(/Check weekly pipeline changes/i),
    ).toBeInTheDocument();
  });

  it("opens the editor modal from the visible panel", () => {
    mockUseHeartbeats.mockReturnValue(
      buildHookResult({ items: [buildHeartbeat()] }),
    );

    render(<HeartbeatPanel workspaceId="ws-1" isAdmin={true} />);
    fireEvent.click(screen.getByTestId("heartbeat-panel-toggle"));

    fireEvent.click(screen.getByTestId("heartbeat-edit-hb-1"));

    expect(screen.getByTestId("heartbeat-editor")).toBeInTheDocument();
  });

  it("fetches workspace heartbeats without a conversation filter", () => {
    mockUseHeartbeats.mockReturnValue(
      buildHookResult({ items: [buildHeartbeat()] }),
    );

    render(<HeartbeatPanel workspaceId="ws-1" isAdmin={true} />);

    expect(mockUseHeartbeats).toHaveBeenCalledWith({
      workspaceId: "ws-1",
      enabled: true,
    });
  });
});
